import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// our components
import NorowsData from './NorowsData'
import Notification from './Notification'
import Carrier from './Carrier'
import OrderStatus from './OrderStatus'
import Customer from './Customer'
import CarrierTracking from './CarrierTracking'

// mui components
import {
    Typography,
    FormControl,
    Pagination,
    Card,
    CardContent,
    Stack,
    Tooltip,
    IconButton,
    Button,
    Backdrop,
    CircularProgress,
    Drawer,
    Box,
    Divider,
    CardHeader,
    Paper,
    Select,
    MenuItem
} from "@mui/material";
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';

import {
    DataGrid,
    GridToolbarContainer
} from '@mui/x-data-grid';

import CloseIcon from '@mui/icons-material/Close';
import Date from './Date'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { fetchInvoiceDetail, resetInvoiceNotifications } from '../../pages/Settings/Invoice/invoiceSlice';
import TrackingDrawer from '../../pages/General/TrackingDrawer';
import OrderDrawer from '../General/OrderDrawer';
import { fetchOrderDetail } from '../../pages/OrderDetail/orderdetailsSlice';
import { fetchCustomerDetails } from '../Reports/Deliveries/trackingEventsSlice';
import WidthMaximize from './WidthMaximize'

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(2),
    boxShadow: 'unset',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    color: theme.palette.text.secondary,
    height: '100%'
}));

// Shared dictionary mapping premium outlined input configurations across sub-modules
const enterpriseInvoiceInputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': { borderColor: '#CBD5E1' },
        '&.Mui-focused': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiSelect-select': {
        padding: '8px 14px !important',
        fontSize: '13px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const InvoiceDetailDrawer = ({ visible, invoiceId,invoiceNumber, invoiceDetailOnClose }) => {
    const dispatch = useDispatch();
    const invoicedetail = useSelector(state => state.invoice.invoiceDetail);
    const profile = useSelector(state => state.auth.profile);
    const [meta, setMeta] = useState(invoicedetail.meta);
    const [drawer, setDrawer] = useState(true);
    const [trackingDrawerVisible, setTrackingDrawerVisible] = useState(false);
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);
    const [isMultiPiece, setIsMultiPiece] = useState(false)
    const [drawerWidth, setDrawerWidth] = useState("80%");
    const columns = [{
        field: 'order_number',
        headerName: 'Order ID #',
        minWidth: 80,
        flex: 1,
        resizable: false, 
        renderCell: (params) => {
            return <span style={{ color: 'rgb(24, 144, 255)', cursor: 'pointer' }} onClick={e => { orderCallback(params.row.order_number) }}>{params.row.order_number}</span>

        }
    },
    {
        field: 'tracking_number',
        headerName: 'Tracking Number #',
        minWidth: 250,
        flex: 1,
        resizable: false, 
        renderCell: (params) => {
            let tracking_number = (params.row.tracking_number
                && typeof params.row.tracking_number !== "undefined" && params.row.tracking_number !== null)
                ? params.row.tracking_number : "";
            let carrier_name = (params.row.carrier_name
                && typeof params.row.carrier_name !== "undefined" && params.row.carrier_name !== null)
                ? params.row.carrier_name : "";
            let tracking = { "tracking_number": tracking_number, "carrier_name": carrier_name }
            return (
                <CarrierTracking trackingItems={tracking} dispatchCallback={trackingCallback} carrierImageWidth="60px" />
            )
        }
    },
    {
        field: 'receiver_name',
        headerName: 'Receiver Name',
        flex: 1,
        resizable: false, 
        renderCell: (params) => {
            return <Customer name={params.row.receiver_name} />
        }
    },
    {
        field: 'receiver_address1',
        headerName: 'Receiver Address',
        flex: 1,
        resizable: false, 
        renderCell: (params) => {
            let addr = ""
            if (params.row.receiver_address1 && typeof params.row.receiver_address1 !== "undefined" && params.row.receiver_address1 !== null) {
                addr = params.row.receiver_address1;
            }
            if (params.row.receiver_address2 && typeof params.row.receiver_address2 !== "undefined" && params.row.receiver_address2 !== null) {
                addr = addr + " " + params.row.receiver_address2
            }
            return addr
        }
    },
    {
        field: 'receiver_city',
        headerName: 'Receiver City',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'receiver_postal_code',
        headerName: 'Receiver Postal Code',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'from',
        headerName: 'From',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'to',
        headerName: 'To',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'pieces',
        headerName: 'Pieces',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'weight',
        headerName: 'Bill Weight',
        width: 80,
        resizable: false, 
        renderCell: (params) => {
            if (params.row && params.row.total_weight && params.row.total_weight != "") {
                return params.row.total_weight
            } else {
                return params.row.weight
            }
        }
    },
    {
        field: 'date_of_shipping',
        headerName: 'Ship Date',
        flex: 1,
        resizable: false, 
        renderCell: (params) => {
            return <Date date={params.row.date_of_shipping} />
        }
    },
    {
        field: 'base_cost',
        headerName: 'Base Cost',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'adjustment',
        headerName: 'Adjustment Cost',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'tax',
        headerName: 'Tax',
        flex: 1,
        resizable: false, 
    },
    {
        field: 'total',
        headerName: 'Total',
        flex: 1,
        resizable: false
    },
    ]


    useEffect(() => {
        if (invoiceId !== '') {
            dispatch(fetchInvoiceDetail(invoiceId, meta))
        }
    }, [meta])

    useEffect(() => {
        setMeta({
            ...meta,
            ...{
                page: 1,
                per_page: 10,
            }
        });
    }, [dispatch, invoiceId])

    const ondrawerclose = () => {
        setDrawer(false)
        invoiceDetailOnClose(false)
    }

    const orderCallback = (item) => {
        dispatch(fetchOrderDetail(item));
        setOrderDrawerVisible(true)
    }

    const orderOnClose = () => {
        setOrderDrawerVisible(false);
    }

    const onExportClick = (invoice_id) => {
        let url = process.env.REACT_APP_API_DOMAIN + '/labelgen/invoice-shipments/export?invoice_id=' + invoice_id+'&invoice_number='+invoiceNumber;
        window.open(url, '_blank')
    }

    const trackingCallback = (carriername, trackingNumber) => {
        let apikey = (profile && profile.api_token) ? profile.api_token : "";
        const data = { 'tracking_number': trackingNumber }
        dispatch(fetchCustomerDetails(data, apikey))
        setTrackingDrawerVisible(true)
    }
    const onClose = () => {
        setTrackingDrawerVisible(false);
    };

    const onSortChange = (currentSorting) => {
        setMeta({
            ...meta,
            ...{
                sorting: JSON.stringify(currentSorting)
            }
        });
    }

    const onChangePerPage = (event) => {
        setMeta({
            ...meta,
            ...{
                page: 1,
                per_page: event.target.value
            }
        });
    }

    const onChangePagination = (event, page) => {
        setMeta({
            ...meta,
            ...{
                page: page
            }
        });
    }

    function CustomTotalRows() {
        return (
            <Box 
                className="custom-total-rows"
                sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    py: 0.6,
                    px: 1.5,
                    borderRadius: '8px',
                    backgroundColor: '#F1F5F9', 
                    border: '1px solid #E2E8F0'
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#475569' }}>
                    Showing <strong style={{ color: '#0F172A', fontWeight: 700 }}>{invoicedetail && invoicedetail.data && invoicedetail.data.length}</strong> of <strong style={{ color: '#0F172A', fontWeight: 700 }}>{(invoicedetail && invoicedetail.meta && invoicedetail.meta.total) ? invoicedetail.meta.total : 0}</strong>
                </Typography>
            </Box>
        )
    }


    function CustomPagesizeoptions() {
        return (
            <Box className="custom-page-size-options" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Show</Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 80, ...enterpriseInvoiceInputStyles }}>
                        <Select
                            value={meta && meta.per_page}
                            onChange={onChangePerPage}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>entries</Typography>
                </Stack>
            </Box>
        )
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer
                sx={{
                    p: 2,
                    bgcolor: '#F8FAFC',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        return (
            <Box
                sx={{
                    p: 1.5,
                    bgcolor: '#F8FAFC',
                    borderTop: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: '100%',
                    boxSizing: 'border-box',
                    '& .MuiPaginationItem-root': {
                        fontSize: '13px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        '&.Mui-selected': {
                            backgroundColor: '#1976d2',
                            color: '#FFFFFF',
                            '&:hover': { backgroundColor: '#115293' }
                        }
                    }
                }}
            >
                <Pagination
                    color="primary"
                    shape="circular"
                    page={invoicedetail && invoicedetail.meta && invoicedetail.meta.current_page}
                    count={invoicedetail && invoicedetail.meta && invoicedetail.meta.last_page}
                    onChange={onChangePagination}
                />
            </Box>
        );
    }

    return (
        <>
            <Drawer
                anchor="right"
                open={visible}
                onClose={ondrawerclose}
                PaperProps={{
                    sx: { 
                        width: drawerWidth,
                        backgroundColor: '#F8FAFC',
                        borderLeft: '1px solid #E2E8F0'
                    },
                }}
            >
                {(invoicedetail && invoicedetail.notification && invoicedetail.notification.show && invoicedetail.notification.messageContent
                    && invoicedetail.notification.messageContent !== "") &&
                    <Notification
                        is_open={invoicedetail.notification.show}
                        messageType={invoicedetail.notification.messageType}
                        messages={invoicedetail.notification.messageContent}
                        handleClose={() => dispatch(resetInvoiceNotifications())}
                    />
                }
                <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
                    <Card 
                        className="sc--cardhead--details"
                        sx={{ 
                            width: '100%',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '16px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ width: '100%', background: '#fff' }}>
                            <CardHeader 
                                title={
                                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px' }}>
                                        Invoice Details
                                    </Typography>
                                } 
                                action={
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Button
                                            variant="outlined"
                                            onClick={() => onExportClick(invoiceId)}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                fontSize: '13px',
                                                height: '36px',
                                                borderColor: '#E2E8F0',
                                                color: '#475569',
                                                '&:hover': {
                                                    borderColor: '#1976d2',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                    color: '#1976d2'
                                                }
                                            }}
                                        >
                                            <FileDownloadOutlinedIcon sx={{ fontSize: '18px', mr: 1, color: "#1976d2" }} /> 
                                            Export
                                        </Button>
                                        <WidthMaximize
                                            defaultWidth="80%"
                                            onToggle={(newWidth) => setDrawerWidth(newWidth)}
                                        />
                                        <IconButton 
                                            aria-label="settings" 
                                            onClick={ondrawerclose}
                                            sx={{ 
                                                color: '#64748B',
                                                '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B' }
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: '20px' }} />
                                        </IconButton>
                                    </Stack>
                                }
                                sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' }}
                            />
                            <DataGrid
                                sx={{
                                    border: 'none',
                                    backgroundColor: '#FFFFFF',
                                    overflow: 'hidden',
                                    '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                                    '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                                    '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' }, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.04)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } } },
                                    '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                                    '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                                }}
                                getRowId={(row) => row.tracking_number+Math.random()}
                                rows={invoicedetail && invoicedetail.data}
                                columns={columns}
                                autoHeight={true}
                                disableColumnFilter={true}
                                disableColumnMenu={true}
                                sortingMode="server"
                                onSortModelChange={(newSortModel) => onSortChange(newSortModel)}
                                sortModel={meta && meta.sorting && JSON.parse(meta.sorting)}
                                disableRowSelectionOnClick
                                slots={{
                                    loadingOverlay: LinearProgress,
                                    footer: CustomPagination,
                                    toolbar: CustomToolbar,
                                    noRowsOverlay: NorowsData
                                }}
                                loading={
                                    (invoicedetail && invoicedetail.loading)
                                }
                            />
                        </Box>
                    </Card>
                </Box>
                <TrackingDrawer
                    visible={trackingDrawerVisible} isMultiPiece={isMultiPiece} onClose={onClose}
                />
                <OrderDrawer
                    visible={orderDrawerVisible} onClose={orderOnClose}
                />
            </Drawer >
        </>
    )
}

export default InvoiceDetailDrawer;